/**
 * Canonical equipment-library domain.
 *
 * This model deliberately separates:
 * - stable product identity;
 * - immutable, digest-bound product revisions;
 * - attributed technical observations;
 * - commercial/market evidence;
 * - explicit admissions into consuming workflows; and
 * - tenant-private products and non-destructive tenant patches.
 *
 * Numerical facts belong in observations, not on mutable product identity
 * records. A solver may consume a revision only through an explicit admission.
 */

export const EQUIPMENT_LIBRARY_SCHEMA_VERSION = 1 as const;
export const EQUIPMENT_LIBRARY_DIGEST_ALGORITHM = 'canonical-json-sha256-v1' as const;

export const EQUIPMENT_LEVELS = [
  'component',
  'packaged-system',
  'plant-configuration',
] as const;
export type EquipmentLevel = typeof EQUIPMENT_LEVELS[number];

export const EQUIPMENT_DOMAINS = [
  'energy-conversion',
  'renewable-generation',
  'nuclear-generation',
  'energy-storage',
  'electrical-balance-of-plant',
  'thermal-balance-of-plant',
  'fluid-balance-of-plant',
  'emissions-control',
  'controls-and-protection',
  'civil-and-logistics',
] as const;
export type EquipmentDomain = typeof EQUIPMENT_DOMAINS[number];

export const EQUIPMENT_KINDS = [
  'gas-turbine',
  'steam-turbine',
  'reciprocating-engine',
  'microturbine',
  'fuel-cell',
  'boiler',
  'heat-recovery-steam-generator',
  'organic-rankine-cycle',
  'solar-module',
  'solar-inverter',
  'solar-tracker',
  'solar-thermal-receiver',
  'wind-turbine',
  'hydro-turbine',
  'pump-turbine',
  'geothermal-power-unit',
  'marine-energy-converter',
  'nuclear-reactor-module',
  'nuclear-steam-supply-system',
  'battery-cell',
  'battery-rack',
  'battery-container',
  'power-conversion-system',
  'energy-management-system',
  'flywheel',
  'thermal-storage-system',
  'synchronous-generator',
  'transformer',
  'switchgear',
  'inverter',
  'pump',
  'compressor',
  'condenser',
  'cooling-tower',
  'heat-exchanger',
  'separator',
  'valve',
  'emissions-control-system',
  'plant-control-system',
  'generation-package',
  'power-island',
  'combined-cycle-plant',
  'chp-plant',
  'renewable-plant',
  'storage-plant',
  'nuclear-plant',
  'grid-interconnection',
  'other',
] as const;
export type EquipmentKind = typeof EQUIPMENT_KINDS[number];

export const EQUIPMENT_TECHNOLOGIES = [
  'natural-gas-combined-cycle',
  'natural-gas-simple-cycle',
  'reciprocating-gas',
  'reciprocating-liquid-fuel',
  'dual-fuel-generation',
  'coal-steam',
  'biomass-steam',
  'waste-to-energy',
  'chp',
  'fuel-cell-generation',
  'solar-pv',
  'solar-csp',
  'wind-onshore',
  'wind-offshore',
  'hydro-francis',
  'hydro-kaplan',
  'hydro-pelton',
  'hydro-bulb',
  'pumped-hydro',
  'geothermal-binary',
  'geothermal-flash',
  'geothermal-dry-steam',
  'marine-tidal',
  'marine-wave',
  'nuclear-pwr',
  'nuclear-bwr',
  'nuclear-htgr',
  'nuclear-molten-salt',
  'nuclear-microreactor',
  'battery-storage',
  'flywheel-storage',
  'thermal-storage',
  'grid-import',
] as const;
export type EquipmentTechnology = typeof EQUIPMENT_TECHNOLOGIES[number];

export const EQUIPMENT_LIFECYCLE_STATUSES = [
  'commercial-current',
  'commercial-legacy',
  'licensed-precommercial',
  'demonstrator',
  'under-construction',
  'announced',
  'status-unconfirmed',
  'retired',
] as const;
export type EquipmentLifecycleStatus = typeof EQUIPMENT_LIFECYCLE_STATUSES[number];

export interface EquipmentDigestFields {
  digestAlgorithm: typeof EQUIPMENT_LIBRARY_DIGEST_ALGORITHM;
  /** SHA-256 of the canonical record payload, excluding these digest fields. */
  contentDigest: string;
}

export interface EquipmentFacets {
  technologies: readonly EquipmentTechnology[];
  energySources: readonly string[];
  fuels: readonly string[];
  primeMovers: readonly string[];
  cycles: readonly string[];
  coolingMethods: readonly string[];
  gridRoles: readonly string[];
  applications: readonly string[];
  regions: readonly string[];
}

interface EquipmentProductBase extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  level: EquipmentLevel;
  domain: EquipmentDomain;
  kind: EquipmentKind;
  manufacturer: string;
  family?: string;
  model: string;
  displayName: string;
  lifecycleStatus: EquipmentLifecycleStatus;
  tags: readonly string[];
  facets: EquipmentFacets;
  currentRevisionId?: string;
  introducedAt?: string;
  retiredAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Platform-curated or public-registry stable product identity. */
export interface EquipmentProduct extends EquipmentProductBase {
  visibility: 'platform' | 'public-registry';
  origin:
    | 'oem-publication'
    | 'public-registry'
    | 'installed-fleet'
    | 'platform-curated'
    | 'planning-baseline';
}

/**
 * Tenant-private identity. Technical evidence remains in private sources and
 * observations; storage paths and signed URLs have no place in this contract.
 */
export interface PrivateEquipmentProduct extends EquipmentProductBase {
  visibility: 'company-private';
  origin: 'company-private';
  companyId: string;
  createdByUid: string;
}

export type EquipmentLibraryProduct = EquipmentProduct | PrivateEquipmentProduct;

export const EQUIPMENT_REVISION_STATUSES = [
  'draft',
  'reviewed',
  'superseded',
  'withdrawn',
] as const;
export type EquipmentRevisionStatus = typeof EQUIPMENT_REVISION_STATUSES[number];

/**
 * Immutable configuration identity. All numerical facts are referenced by
 * observation id so variant, basis, and citation can never be flattened away.
 */
export interface EquipmentRevision extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  productId: string;
  revisionLabel: string;
  variantLabel: string;
  status: EquipmentRevisionStatus;
  configurationLevel: EquipmentLevel;
  frequencyHz: readonly (50 | 60)[];
  fuels: readonly string[];
  duties: readonly string[];
  sourceIds: readonly string[];
  observationIds: readonly string[];
  supersedesRevisionId?: string;
  manufacturerRevision?: string;
  effectiveAt?: string;
  publishedAt?: string;
  compiledAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
}

export const EQUIPMENT_SOURCE_KINDS = [
  'product-page',
  'datasheet',
  'product-guide',
  'application-manual',
  'technical-report',
  'regulatory-filing',
  'certified-registry',
  'installed-fleet-dataset',
  'proposal',
  'quote',
  'private-document',
  'planning-study',
] as const;
export type EquipmentSourceKind = typeof EQUIPMENT_SOURCE_KINDS[number];

export const EQUIPMENT_SOURCE_ACCESS = [
  'public',
  'registration-required',
  'proposal-required',
  'company-private',
] as const;
export type EquipmentSourceAccess = typeof EQUIPMENT_SOURCE_ACCESS[number];

interface EquipmentSourceBase extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  kind: EquipmentSourceKind;
  publisher: string;
  title: string;
  revision?: string;
  publishedAt?: string;
  retrievedAt: string;
  access: EquipmentSourceAccess;
  contentSha256?: string;
  license?: string;
  copyrightNotice?: string;
  notes?: string;
}

export interface PublicEquipmentSource extends EquipmentSourceBase {
  visibility: 'public';
  url: string;
  archiveUrl?: string;
}

export interface PrivateEquipmentSource extends EquipmentSourceBase {
  visibility: 'company-private';
  access: 'company-private';
  companyId: string;
  documentId: string;
  fileName: string;
}

export type EquipmentSource = PublicEquipmentSource | PrivateEquipmentSource;

export const EQUIPMENT_METRICS = [
  'electrical-output',
  'thermal-output',
  'fuel-input',
  'heat-rate',
  'electrical-efficiency',
  'thermal-efficiency',
  'total-chp-efficiency',
  'auxiliary-load',
  'minimum-load',
  'ramp-rate',
  'start-time',
  'availability',
  'forced-outage-rate',
  'capacity-factor',
  'energy-capacity',
  'discharge-duration',
  'round-trip-efficiency',
  'depth-of-discharge',
  'state-of-charge',
  'c-rate',
  'cycle-life',
  'annual-degradation',
  'exhaust-mass-flow',
  'exhaust-temperature',
  'steam-production',
  'steam-pressure',
  'steam-temperature',
  'water-withdrawal',
  'water-consumption',
  'co2-emissions',
  'co2e-emissions',
  'ch4-emissions',
  'n2o-emissions',
  'nox-emissions',
  'so2-emissions',
  'co-emissions',
  'pm10-emissions',
  'pm2.5-emissions',
  'voc-emissions',
  'methane-slip',
  'sound-power',
  'sound-pressure',
  'length',
  'width',
  'height',
  'mass',
  'installed-footprint',
  'rated-kva',
  'voltage',
  'power-factor',
  'frequency',
  'rotational-speed',
  'reactance-xd',
  'reactance-xd-prime',
  'reactance-xd-double-prime',
  'reactance-xq',
  'reactance-xq-double-prime',
  'negative-sequence-reactance',
  'zero-sequence-reactance',
  'time-constant',
  'rotor-inertia',
  'short-circuit-ratio',
  'rotor-diameter',
  'hub-height',
  'cut-in-speed',
  'cut-out-speed',
  'module-efficiency',
  'temperature-coefficient',
  'head',
  'flow',
  'thermal-power',
  'refueling-interval',
  'design-life',
] as const;
export type StandardEquipmentMetric = typeof EQUIPMENT_METRICS[number];
export type EquipmentMetric = StandardEquipmentMetric | `custom:${string}`;

export type EquipmentObservedValue =
  | { kind: 'scalar'; value: number }
  | { kind: 'range'; low: number; high: number }
  | {
      kind: 'curve';
      xUnit: string;
      yUnit: string;
      points: readonly { x: number; y: number }[];
    }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'text'; value: string };

export interface EquipmentObservationBasis {
  qualifier: 'minimum' | 'maximum' | 'nominal' | 'typical' | 'guaranteed' | 'range';
  outputBoundary?:
    | 'engine-shaft'
    | 'generator-terminals'
    | 'package-gross'
    | 'package-net'
    | 'plant-gross'
    | 'plant-net'
    | 'grid-interconnection';
  heatingValueBasis?: 'LHV' | 'HHV' | 'not-applicable' | 'not-stated';
  lifecycleBoundary?:
    | 'direct-operation'
    | 'upstream-fuel'
    | 'construction'
    | 'decommissioning'
    | 'cradle-to-gate'
    | 'cradle-to-grave';
  frequencyHz?: 50 | 60;
  duty?: string;
  fuel?: string;
  emissionsControl?: string;
  referenceOxygenPct?: number;
  dryWetBasis?: 'dry' | 'wet' | 'not-stated';
  grossNetBasis?: 'gross' | 'net' | 'not-applicable' | 'not-stated';
  referenceConditions?: string;
  coolingMethod?: string;
  configuration?: string;
  notes?: string;
}

export const EQUIPMENT_EVIDENCE_STATUSES = [
  'published',
  'manufacturer-submitted',
  'derived-from-published',
  'observed-fleet',
  'planning-assumption',
  'proposal-specific',
  'conflicting-sources',
  'unverified',
] as const;
export type EquipmentEvidenceStatus = typeof EQUIPMENT_EVIDENCE_STATUSES[number];

export interface EquipmentObservationSourceRef {
  sourceId: string;
  locator?: string;
  quoteDigest?: string;
}

/** One attributed technical fact at one exact product revision. */
export interface EquipmentObservation extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  revisionId: string;
  metric: EquipmentMetric;
  value: EquipmentObservedValue;
  /** UCUM-compatible or explicit engineering unit; use "1" for dimensionless. */
  unit: string;
  basis: EquipmentObservationBasis;
  sourceRefs: readonly EquipmentObservationSourceRef[];
  evidenceStatus: EquipmentEvidenceStatus;
  confidence: 'low' | 'medium' | 'high';
  observedAt?: string;
  validFrom?: string;
  validTo?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  derivation?: {
    method: string;
    inputObservationIds: readonly string[];
  };
  notes?: string;
}

export const EQUIPMENT_ADMISSION_SURFACES = [
  'browse',
  'equipment-bom',
  'generation-screening',
  'thermodynamic-solver',
  'electrical-study',
  'layout-test-fit',
  'financial-model',
] as const;
export type EquipmentAdmissionSurface = typeof EQUIPMENT_ADMISSION_SURFACES[number];

export const EQUIPMENT_ADMISSION_STATUSES = [
  'admitted',
  'comparison-only',
  'needs-data',
  'rejected',
  'retired',
] as const;
export type EquipmentAdmissionStatus = typeof EQUIPMENT_ADMISSION_STATUSES[number];

/** Explicit governance decision; catalog presence never implies solver admission. */
export interface EquipmentAdmission extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  revisionId: string;
  surface: EquipmentAdmissionSurface;
  status: EquipmentAdmissionStatus;
  adapterId?: string;
  adapterVersion?: string;
  requiredObservationIds: readonly string[];
  missingMetrics: readonly EquipmentMetric[];
  conditions: readonly string[];
  reason: string;
  reviewedAt: string;
  reviewedBy: string;
  supersedesAdmissionId?: string;
}

interface EquipmentMarketEvidenceBase extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  productId: string;
  revisionId?: string;
  supplierId?: string;
  geography: string;
  sourceIds: readonly string[];
  evidenceStatus: EquipmentEvidenceStatus;
  confidence: 'low' | 'medium' | 'high';
  validFrom?: string;
  validTo?: string;
  observedAt: string;
  notes?: string;
}

export type EquipmentMarketEvidence =
  | (EquipmentMarketEvidenceBase & {
      evidenceKind: 'price';
      amount: number;
      currency: string;
      priceBasis: string;
    })
  | (EquipmentMarketEvidenceBase & {
      evidenceKind: 'lead-time';
      lowMonths: number;
      highMonths: number;
      leadTimeBasis: string;
    })
  | (EquipmentMarketEvidenceBase & {
      evidenceKind: 'production-slot';
      availability: 'available' | 'limited' | 'unavailable' | 'reserved' | 'unknown';
      deliveryWindow?: string;
    })
  | (EquipmentMarketEvidenceBase & {
      evidenceKind: 'warranty';
      durationYears: number;
      coverage: string;
    });

export const LEGACY_EQUIPMENT_NAMESPACES = [
  'equipment-catalog',
  'oem-generation-product',
  'oem-generation-performance-case',
  'oem-electrical-machine',
  'oem-electrical-rating-case',
  'generation-screening-catalog',
  'reactor-vendor-catalog',
] as const;
export type LegacyEquipmentNamespace = typeof LEGACY_EQUIPMENT_NAMESPACES[number];

/** Stable bridge retained until every consumer has migrated to canonical ids. */
export interface LegacyEquipmentIdLink extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  namespace: LegacyEquipmentNamespace;
  legacyId: string;
  productId: string;
  revisionId?: string;
  mapping: 'exact' | 'family-alias' | 'configuration-alias';
  frozenSnapshotDigest?: string;
  createdAt: string;
  notes?: string;
}

/**
 * Tenant patch fields are intentionally non-technical. Private observations and
 * market evidence are layered by id; canonical OEM facts are never overwritten.
 */
export interface CompanyEquipmentPatch extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  companyId: string;
  targetProductId: string;
  baseProductDigest: string;
  baseRevisionId?: string;
  baseRevisionDigest?: string;
  patch: {
    displayName?: string;
    tags?: readonly string[];
    preferredSupplierIds?: readonly string[];
    privateObservationIds?: readonly string[];
    privateMarketEvidenceIds?: readonly string[];
    privateNotes?: string;
    active?: boolean;
  };
  createdAt: string;
  updatedAt: string;
  updatedByUid: string;
}

export interface PlanningBenchmarkMetric {
  id: string;
  metric: EquipmentMetric;
  value: EquipmentObservedValue;
  unit: string;
  basis: EquipmentObservationBasis;
  sourceIds: readonly string[];
  confidence: 'low' | 'medium' | 'high';
  notes?: string;
}

/** Non-OEM planning evidence, kept impossible to confuse with a product fact. */
export interface EquipmentPlanningBenchmark extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  id: string;
  name: string;
  technology: EquipmentTechnology;
  geography: string;
  scenario: string;
  validFrom?: string;
  validTo?: string;
  sourceIds: readonly string[];
  metrics: readonly PlanningBenchmarkMetric[];
  compiledAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

export interface EquipmentLibraryBundle extends EquipmentDigestFields {
  schemaVersion: typeof EQUIPMENT_LIBRARY_SCHEMA_VERSION;
  compiledAt: string;
  sources: readonly EquipmentSource[];
  products: readonly EquipmentLibraryProduct[];
  revisions: readonly EquipmentRevision[];
  observations: readonly EquipmentObservation[];
  admissions: readonly EquipmentAdmission[];
  marketEvidence: readonly EquipmentMarketEvidence[];
  companyPatches: readonly CompanyEquipmentPatch[];
  legacyIds: readonly LegacyEquipmentIdLink[];
  planningBenchmarks: readonly EquipmentPlanningBenchmark[];
}
